import type {
  ExpenseCategory,
  ExpenseFormRow,
  ExpenseRecord,
  PaymentFormRow,
  PaymentRecord,
  Trip,
  TripFinancials,
  TripFormData,
  TripSavePayload,
} from "./types";
import { buildTripFromForm, calculateFinancials, toNumber } from "./rules";
import { generateId } from "../shared/id";

const getLocalDate = (): string => {
  const now = new Date();
  const offsetDate = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return offsetDate.toISOString().slice(0, 10);
};

export const createPaymentFormRow = (date: string): PaymentFormRow => ({
  id: generateId(),
  date,
  amount: "",
  mode: "Cash",
  note: "",
});

export const createExpenseFormRow = (
  date: string,
  category: ExpenseCategory = "Fuel"
): ExpenseFormRow => ({
  id: generateId(),
  date,
  category,
  amount: "",
  note: "",
});

export const createEmptyTripForm = (): TripFormData => ({
  tripNo: "",
  date: getLocalDate(),
  bookingType: "Direct",
  vehicleSource: "Own Vehicle",
  brokerName: "",
  vehicleNo: "",
  driverName: "",
  driverMobile: "",
  partyName: "",
  origin: "",
  destination: "",
  hireAmount: "",
  advanceReceived: "",
  fuelExp: "",
  fastagExp: "",
  driverBata: "",
  commissionExp: "",
  otherExp: "",
  status: "Planned",
  notes: "",
  payments: [],
  expenses: [],
});

const paymentRowsFromTrip = (trip: Trip): PaymentFormRow[] =>
  trip.advanceReceived > 0
    ? [
        {
          id: `${trip.id}-payment-advance`,
          date: trip.date,
          amount: String(trip.advanceReceived),
          mode: "Cash",
          note: "Advance received",
        },
      ]
    : [];

const expenseRowsFromTrip = (trip: Trip): ExpenseFormRow[] =>
  [
    { category: "Fuel" as const, amount: trip.fuelExp },
    { category: "FASTag" as const, amount: trip.fastagExp },
    { category: "Driver Bata" as const, amount: trip.driverBata },
    { category: "Commission" as const, amount: trip.commissionExp },
    { category: "Other" as const, amount: trip.otherExp },
  ]
    .filter((expense) => expense.amount > 0)
    .map((expense) => ({
      id: `${trip.id}-expense-${expense.category.toLowerCase().replace(/\s+/g, "-")}`,
      date: trip.date,
      category: expense.category,
      amount: String(expense.amount),
      note: "",
    }));

const paymentRecordToFormRow = (payment: PaymentRecord): PaymentFormRow => ({
  id: payment.id,
  date: payment.date,
  amount: String(payment.amount || ""),
  mode: payment.mode,
  note: payment.note,
});

const expenseRecordToFormRow = (expense: ExpenseRecord): ExpenseFormRow => ({
  id: expense.id,
  date: expense.date,
  category: expense.category,
  amount: String(expense.amount || ""),
  note: expense.note,
});

export const tripToForm = (
  trip: Trip,
  payments: PaymentRecord[] = [],
  expenses: ExpenseRecord[] = []
): TripFormData => ({
  tripNo: trip.tripNo,
  date: trip.date,
  bookingType: trip.bookingType,
  vehicleSource: trip.vehicleSource,
  brokerName: trip.brokerName,
  vehicleNo: trip.vehicleNo,
  driverName: trip.driverName,
  driverMobile: trip.driverMobile,
  partyName: trip.partyName,
  origin: trip.origin,
  destination: trip.destination,
  hireAmount: String(trip.hireAmount || ""),
  advanceReceived: String(trip.advanceReceived || ""),
  fuelExp: String(trip.fuelExp || ""),
  fastagExp: String(trip.fastagExp || ""),
  driverBata: String(trip.driverBata || ""),
  commissionExp: String(trip.commissionExp || ""),
  otherExp: String(trip.otherExp || ""),
  status: trip.status,
  notes: trip.notes,
  payments:
    payments.length > 0 ? payments.map(paymentRecordToFormRow) : paymentRowsFromTrip(trip),
  expenses:
    expenses.length > 0 ? expenses.map(expenseRecordToFormRow) : expenseRowsFromTrip(trip),
});

export const repeatTripToForm = (trip: Trip): TripFormData => ({
  ...tripToForm(trip),
  tripNo: "",
  date: getLocalDate(),
  advanceReceived: "",
  fuelExp: "",
  fastagExp: "",
  driverBata: "",
  commissionExp: "",
  otherExp: "",
  status: "Planned",
  payments: [],
  expenses: [],
});

const paymentTotalFromForm = (form: TripFormData): number =>
  form.payments.reduce((sum, payment) => sum + toNumber(payment.amount), 0);

const expenseTotalsFromForm = (
  form: TripFormData
): Pick<
  TripFormData,
  "fuelExp" | "fastagExp" | "driverBata" | "commissionExp" | "otherExp"
> =>
  form.expenses.reduce(
    (acc, expense) => {
      const amount = toNumber(expense.amount);
      if (expense.category === "Fuel") acc.fuelExp = String(toNumber(acc.fuelExp) + amount);
      else if (expense.category === "FASTag") {
        acc.fastagExp = String(toNumber(acc.fastagExp) + amount);
      } else if (expense.category === "Driver Bata") {
        acc.driverBata = String(toNumber(acc.driverBata) + amount);
      } else if (expense.category === "Commission") {
        acc.commissionExp = String(toNumber(acc.commissionExp) + amount);
      } else {
        acc.otherExp = String(toNumber(acc.otherExp) + amount);
      }
      return acc;
    },
    {
      fuelExp: "0",
      fastagExp: "0",
      driverBata: "0",
      commissionExp: "0",
      otherExp: "0",
    }
  );

const formWithLineItemTotals = (form: TripFormData): TripFormData => {
  const hasPaymentRows = form.payments.length > 0;
  const hasExpenseRows = form.expenses.length > 0;
  const expenseTotals = hasExpenseRows ? expenseTotalsFromForm(form) : {};

  return {
    ...form,
    ...(hasPaymentRows ? { advanceReceived: String(paymentTotalFromForm(form)) } : {}),
    ...expenseTotals,
  };
};

export const getFormPreview = (form: TripFormData): TripFinancials =>
  calculateFinancials({
    hireAmount: toNumber(formWithLineItemTotals(form).hireAmount),
    advanceReceived: toNumber(formWithLineItemTotals(form).advanceReceived),
    fuelExp: toNumber(formWithLineItemTotals(form).fuelExp),
    fastagExp: toNumber(formWithLineItemTotals(form).fastagExp),
    driverBata: toNumber(formWithLineItemTotals(form).driverBata),
    commissionExp: toNumber(formWithLineItemTotals(form).commissionExp),
    otherExp: toNumber(formWithLineItemTotals(form).otherExp),
    status: form.status,
  });

export const formToTrip = (form: TripFormData, existingId?: string): Trip =>
  formToTripSavePayload(form, existingId).trip;

export const formToTripSavePayload = (
  form: TripFormData,
  existingId?: string
): TripSavePayload => {
  const tripId = existingId ?? generateId();
  const aggregateForm = formWithLineItemTotals(form);
  const trip = buildTripFromForm(aggregateForm, tripId);

  return {
    trip,
    payments: form.payments
      .filter((payment) => toNumber(payment.amount) > 0)
      .map((payment) => ({
        id: payment.id || generateId(),
        tripId,
        date: payment.date || form.date,
        amount: toNumber(payment.amount),
        mode: payment.mode,
        note: payment.note.trim(),
      })),
    expenses: form.expenses
      .filter((expense) => toNumber(expense.amount) > 0)
      .map((expense) => ({
        id: expense.id || generateId(),
        tripId,
        date: expense.date || form.date,
        category: expense.category,
        amount: toNumber(expense.amount),
        note: expense.note.trim(),
      })),
  };
};
